import update from 'immutability-helper';

import { sub, isAfter, lastDayOfMonth, addMonths, differenceInCalendarMonths } from 'date-fns';
import Defaults from 'mangools-commons/dist/constants/Defaults';

import reduce from 'ramda/src/reduce';
import takeLast from 'ramda/src/takeLast';
import pipe from 'ramda/src/pipe';
import map from 'ramda/src/map';
import reverse from 'ramda/src/reverse';
import dropLastWhile from 'ramda/src/dropLastWhile';
import last from 'ramda/src/last';

import searchVolumeTimeframeTypes from 'constants/SearchVolumeTimeframeTypes';

import { SearchVolumeService } from 'services/SearchVolumeService';

const { LAST_12_MONTHS, LAST_6_MONTHS, LAST_3_MONTHS, LAST_KNOWN_VALUES } = searchVolumeTimeframeTypes;

// Google reports a month with a delay of a month or two, so only a last volume older than this,
// counted from when we last fetched the keyword, means Google stopped reporting it.
const NO_VOLUME_AFTER_MONTHS = 3;

// The month after the last month with a volume, or null while the history is still current.
// Compared with the fetch time and not with today: a keyword we have not re-fetched for months
// (e.g. in a saved list) has an old history too, but Google may still report it.
const getNoVolumeReportedSince = (lastMsv, fetchedAt) => {
    if (!lastMsv || !(fetchedAt > 0)) {
        return null;
    }

    const lastMonth = new Date(lastMsv.year, lastMsv.month);

    if (differenceInCalendarMonths(new Date(fetchedAt * 1000), lastMonth) <= NO_VOLUME_AFTER_MONTHS) {
        return null;
    }

    const since = addMonths(lastMonth, 1);
    return { year: since.getFullYear(), month: since.getMonth() };
};

class ResultDataService {
    /* eslint-disable no-shadow */
    static calculateSearchVolumeTimeframes(data) {
        const currentDate = new Date();

        return map(({ searchCount, ...resultRest }) => {
            // Empty months at the end of the history are not known values (Google stopped reporting a volume),
            // so "Last known" averages the 12 months up to the last month that has one.
            const lastKnownMsv = pipe(
                dropLastWhile(msv => !(msv.count > 0)),
                takeLast(12),
            )(resultRest.monthlySearchVolumes);
            const lastKnown = reduce(
                (acc, msv) => ({
                    entriesCount: acc.entriesCount + 1,
                    sum: acc.sum + msv.count,
                }),
                { entriesCount: 0, sum: 0 },
                lastKnownMsv,
            );

            const searchVolumes = pipe(
                takeLast(12),
                reverse,
                reduce(
                    (acc, msv) => {
                        const msvDate = new Date(
                            msv.year,
                            msv.month,
                            lastDayOfMonth(new Date(msv.year, msv.month)).getDate(),
                        );

                        const isLast6m = isAfter(
                            msvDate,
                            sub(currentDate, { months: 6, days: currentDate.getUTCDate() }),
                        );
                        const isLast3m = isAfter(
                            msvDate,
                            sub(currentDate, { months: 3, days: currentDate.getUTCDate() }),
                        );

                        const last6m = isLast6m
                            ? {
                                entriesCount: acc[LAST_6_MONTHS].entriesCount + 1,
                                sum: acc[LAST_6_MONTHS].sum + msv.count,
                            }
                            : acc[LAST_6_MONTHS];
                        const last3m = isLast3m
                            ? {
                                entriesCount: acc[LAST_3_MONTHS].entriesCount + 1,
                                sum: acc[LAST_3_MONTHS].sum + msv.count,
                            }
                            : acc[LAST_3_MONTHS];

                        return {
                            [LAST_6_MONTHS]: last6m,
                            [LAST_3_MONTHS]: last3m,
                        };
                    },
                    {
                        [LAST_6_MONTHS]: { entriesCount: 0, sum: 0 },
                        [LAST_3_MONTHS]: { entriesCount: 0, sum: 0 },
                    },
                ),
                result => {
                    return update({ ...result, [LAST_KNOWN_VALUES]: lastKnown }, {
                        [LAST_12_MONTHS]: { $set: searchCount },
                        [LAST_KNOWN_VALUES]: {
                            $apply: data => {
                                if (data.sum === 0) {
                                    return Defaults.NO_VALUE;
                                } else {
                                    return SearchVolumeService.roundAverage(Math.floor(data.sum / data.entriesCount));
                                }
                            },
                        },
                        [LAST_6_MONTHS]: {
                            $apply: data => {
                                if (data.sum === 0) {
                                    return Defaults.NO_VALUE;
                                } else {
                                    return SearchVolumeService.roundAverage(Math.floor(data.sum / data.entriesCount));
                                }
                            },
                        },
                        [LAST_3_MONTHS]: {
                            $apply: data => {
                                if (data.sum === 0) {
                                    return Defaults.NO_VALUE;
                                } else {
                                    return SearchVolumeService.roundAverage(Math.floor(data.sum / data.entriesCount));
                                }
                            },
                        },
                    });
                },
            )(resultRest.monthlySearchVolumes);

            return update(resultRest, {
                searchVolumesByTimeframe: { $set: searchVolumes },
                noVolumeReportedSince: { $set: getNoVolumeReportedSince(last(lastKnownMsv), resultRest.fetchedAt) },
            });
        }, data);
    }

    static calculateSearchVolumeTimeframesForCompetitor(data) {
        return map(({ searchCount, ...resultRest }) => {
            return update(resultRest, {
                searchVolumesByTimeframe: {
                    $set: {
                        [LAST_KNOWN_VALUES]: Defaults.NO_VALUE,
                        [LAST_12_MONTHS]: searchCount,
                        [LAST_6_MONTHS]: Defaults.NO_VALUE,
                        [LAST_3_MONTHS]: Defaults.NO_VALUE,
                    },
                },
            });
        }, data);
    }
}

export default ResultDataService;
